import '../i18n';
import '../styles/globals.css';

import { Auth0Provider, useAuth0 } from '@auth0/auth0-react';
import { ROUTER_NAME } from '@consts';
import { Layout } from '@shared';
import { setupForDark } from '@theme';
import classNames from 'classnames';
import Head from 'next/head';
import { useRouter } from 'next/router';
import { MultiBackend } from 'react-dnd-multi-backend';
import { ToastContainer } from 'react-toastify';
// import { HTML5Backend } from 'react-dnd-html5-backend';
// import { TouchBackend } from 'react-dnd-touch-backend';
import BrandLoader from '@basic/BrandLoader';
import { useWebView } from '@hooks';
import { HTML5toTouch } from 'rdndmb-html5-to-touch';
import { DndProvider } from 'react-dnd';
import ApprovalsGuard from '../components/shared/ApprovalsGuard';
import AuthGuard from '../components/shared/AuthGuard';
import BackgroundActivityNavigationGuard from '../components/shared/BackgroundActivityNavigationGuard';
import BackgroundActivityProvider from '../components/shared/BackgroundActivityProvider';
import { BlockOutputsFlusher } from '../components/shared/BlockOutputsFlusher';
import CookieBanner from '../components/shared/CookieBanner';
import NotificationsGuard from '../components/shared/NotificationsGuard';
import ToastProvider from '../components/shared/ToastProvider';
import TranscriptReadyToastListener from '../components/shared/TranscriptReadyToastListener';
/* eslint-disable react/prop-types */

// Create a whitelist of public routes that don't need auth
const PUBLIC_ROUTES = [
  ROUTER_NAME.NOT_FOUND,
  ROUTER_NAME.VERIFY_EMAIL,
  // PayU payment-link buyers land here unauthenticated, so it must skip AuthGuard.
  ROUTER_NAME.PAYMENT_LINK_THANK_YOU,
  // Public Polish-only legal pages — no auth, no app chrome.
  ROUTER_NAME.TERMS,
  ROUTER_NAME.PRIVACY_POLICY,
  ROUTER_NAME.DATA_PROTECTION_OFFICER,
  ROUTER_NAME.RETURN_POLICY,
  ROUTER_NAME.CONTACT
  // Add other public routes here
];
const OPTIONAL_AUTH_ROUTES = [
  ROUTER_NAME.GOAL,
  ROUTER_NAME.GOAL_THANK_YOU,
  ROUTER_NAME.GOAL_PACKAGE,
  ROUTER_NAME.EXPLORE,
  ROUTER_NAME.GOAL_SUBSCRIPTION_VERIFICATION,
  ROUTER_NAME.PAYMENT_THANK_YOU
];

/**
 * Routes all non-public pages through the correct auth path.
 * Authenticated users always render through AuthGuard (even on optional auth routes)
 * so that AuthGuard stays mounted when navigating between explore and protected pages.
 * Unauthenticated users on optional auth routes render without AuthGuard.
 */
function AppAuthRouter({ Component, pageProps, getLayout, toastContainerEl, isOptionalAuthRoute }) {
  const { isAuthenticated, isLoading } = useAuth0();

  if (isOptionalAuthRoute && !isAuthenticated && !isLoading) {
    return (
      <ToastProvider>
        {getLayout(<Component {...pageProps} />)}
        {toastContainerEl}
      </ToastProvider>
    );
  }

  return (
    <AuthGuard>
      <ApprovalsGuard>
        <NotificationsGuard>
          <BlockOutputsFlusher />
          <TranscriptReadyToastListener />
          <ToastProvider>
            <DndProvider backend={MultiBackend} options={HTML5toTouch}>
              {getLayout(<Component {...pageProps} />)}
              {toastContainerEl}
            </DndProvider>
          </ToastProvider>
        </NotificationsGuard>
      </ApprovalsGuard>
    </AuthGuard>
  );
}

export default function App({ Component, pageProps }) {
  const router = useRouter();
  const { isWebView, isDetectingWebView, isAuthReady } = useWebView();

  const getLayout = Component.getLayout || ((page) => <Layout>{page}</Layout>);
  const onRedirectCallback = (appState, searchParams) => {
    router.replace(appState?.returnTo || '/');
  };

  const isPublicRoute = PUBLIC_ROUTES.includes(router.pathname);
  const isOptionalAuthRoute = OPTIONAL_AUTH_ROUTES.includes(router.pathname);

  // If still detecting WebView status or auth is not ready, show loading
  if (isDetectingWebView || (isWebView && !isAuthReady)) {
    return <BrandLoader />;
  }

  const toastContainerEl = (
    <ToastContainer
      position='top-right'
      autoClose={3000}
      limit={2}
      hideProgressBar
      newestOnTop={false}
      closeButton={false}
      closeOnClick
      rtl={false}
      pauseOnFocusLoss
      draggable
      pauseOnHover
      toastClassName={({ defaultClassName }) =>
        classNames(
          defaultClassName,
          'P2M',
          'p-md',
          'rounded-sm',
          'flex flex-row gap-sm',
          'overflow-hidden cursor-pointer',
          setupForDark('!text-secondary !bg-modal-secondary')
        )
      }
    />
  );

  return (
    <>
      <Head>
        {/* prevent zoom on input focus on iOS -> https://stackoverflow.com/a/34085554 */}
        {/* viewport meta must live in app.jsx see: https://nextjs.org/docs/messages/no-document-viewport-meta */}
        <meta
          name='viewport'
          content='width=device-width, initial-scale=1, maximum-scale=1, user-scalable=0'
        />
      </Head>
      <Auth0Provider
        domain={process.env.NEXT_PUBLIC_AUTH0_ISSUER_BASE_URL}
        clientId={process.env.NEXT_PUBLIC_AUTH0_CLIENT_ID}
        onRedirectCallback={onRedirectCallback}
        authorizationParams={{
          redirect_uri: process.env.NEXT_PUBLIC_AUTH0_BASE_URL,
          audience: process.env.NEXT_PUBLIC_AUTH0_AUDIENCE,
          scope: 'openid profile email'
        }}
        useRefreshTokens={true}
        cacheLocation='localstorage'
      >
        <CookieBanner />
        {/* PUBLIC ROUTES */}
        {isPublicRoute && <Component {...pageProps} />}

        {/* NON-PUBLIC ROUTES: authenticated users always go through AuthGuard,
            unauthenticated users on optional auth routes render without it.
            BackgroundActivityProvider holds the registry + upload service for
            the lifetime of the app so uploads survive any subtree unmount. */}
        {!isPublicRoute && (
          <BackgroundActivityProvider>
            <BackgroundActivityNavigationGuard />
            <AppAuthRouter
              Component={Component}
              pageProps={pageProps}
              getLayout={getLayout}
              toastContainerEl={toastContainerEl}
              isOptionalAuthRoute={isOptionalAuthRoute}
            />
          </BackgroundActivityProvider>
        )}
      </Auth0Provider>
    </>
  );
}
